import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { FileText, Plus, Search, Edit, Trash2, Eye, Calendar, Loader2, RefreshCw, ShoppingCart, User, Package, DollarSign, TrendingUp, Copy } from "lucide-react";
import { toast } from "sonner";
import { salesOrderApiService, SalesOrder, SalesOrderDetail } from "../services/salesOrderApi";
import { PermissionGate } from "./ui/PermissionGate";
import { useAuth } from "../contexts/AuthContext";
import { useBranch } from "../contexts/BranchContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { getCurrentDate, getFutureDate, formatDateForForm } from "../utils/dateUtils";

export function SalesOrderListingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [searchTerm, setSearchTerm] = useState('');
  const [allSalesOrders, setAllSalesOrders] = useState<SalesOrder[]>([]);
  const [filteredSalesOrders, setFilteredSalesOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [orderToClone, setOrderToClone] = useState<SalesOrder | null>(null);
  const [cloning, setCloning] = useState(false);
  const [cloneFormData, setCloneFormData] = useState({
    orderDate: getCurrentDate(),
    effectiveStartDate: getCurrentDate(),
    effectiveEndDate: getFutureDate(10),
    refNo: '',
    refDate: '',
  });

  // Sales Order management route (from backend permissions)
  const SALES_ORDER_ROUTE = '/sales-orders';

  // Load all sales orders (client-side pagination)
  const loadSalesOrders = useCallback(async () => {
    try {
      setLoading(true);
      const activeBranchId = selectedBranch || user?.defaultBranchId;
      // Fetch all sales orders at once for client-side pagination
      const response = await salesOrderApiService.getSalesOrdersData(1, 1000, activeBranchId); // Large page size to get all data
      
      setAllSalesOrders(response.salesOrders);
    } catch (error) {
      // console.error('Error loading sales orders:', error);
      toast.error('Failed to load sales orders');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, user?.defaultBranchId]);

  // Filter sales orders based on search term
  const filterSalesOrders = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredSalesOrders(allSalesOrders);
    } else {
      const filtered = allSalesOrders.filter(order =>
        order.soNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSalesOrders(filtered);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [allSalesOrders, searchTerm]);


  // Load data on component mount
  useEffect(() => {
    loadSalesOrders();
  }, [loadSalesOrders]);

  // Filter data when search term or all sales orders change
  useEffect(() => {
    filterSalesOrders();
  }, [filterSalesOrders]);

  // Handle search
  const handleSearch = () => {
    filterSalesOrders();
  };

  // Handle refresh
  const handleRefresh = () => {
    loadSalesOrders();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Calculate pagination data
  const totalCount = filteredSalesOrders.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = filteredSalesOrders.slice(startIndex, endIndex);

  // Handle new sales order
  const handleNewOrder = () => {
    navigate('/sales-orders/new', { 
      state: { mode: 'create' } 
    });
  };

  // Handle edit sales order
  const handleEditOrder = (order: SalesOrder) => {
    navigate(`/sales-orders/${order.id}/edit`, { 
      state: { mode: 'edit', salesOrder: order } 
    });
  };

  // Handle view sales order
  const handleViewOrder = (order: SalesOrder) => {
    navigate(`/sales-orders/${order.id}/view`, { 
      state: { mode: 'view', salesOrder: order } 
    });
  };

  // Handle delete sales order
  const handleDeleteOrder = async (order: SalesOrder) => {
    if (window.confirm(`Are you sure you want to delete sales order ${order.soNumber}?`)) {
      try {
        await salesOrderApiService.deleteSalesOrder(order.id.toString());
        toast.success('Sales order deleted successfully');
        // Remove from all sales orders and refresh
        setAllSalesOrders(prev => prev.filter(o => o.id !== order.id));
      } catch (error) {
        // console.error('Error deleting sales order:', error);
        toast.error('Failed to delete sales order');
      }
    }
  };

  // Handle clone sales order - open dialog
  const handleCloneOrder = (order: SalesOrder) => {
    setOrderToClone(order);
    setCloneFormData({
      orderDate: getCurrentDate(),
      effectiveStartDate: formatDateForForm(order.effectiveStartDate) || getCurrentDate(),
      effectiveEndDate: formatDateForForm(order.effectiveEndDate) || getFutureDate(10),
      refNo: order.refNo || '',
      refDate: '',
    });
    setCloneDialogOpen(true);
  };

  // Handle clone sales order - execute clone
  const handleCloneSubmit = async () => {
    if (!orderToClone) return;

    try {
      setCloning(true);
      
      // Fetch full order details including line items
      const orderResponse = await salesOrderApiService.getSalesOrderById(orderToClone.id.toString());
      if (!orderResponse.success || !orderResponse.data) {
        throw new Error('Failed to fetch order details');
      }

      const fullOrder = orderResponse.data;

      // Fetch line items
      const detailsResponse = await salesOrderApiService.getSalesOrderDetails(orderToClone.id.toString());
      if (!detailsResponse.success) {
        throw new Error('Failed to fetch order line items');
      }

      const lineItems = detailsResponse.data || [];

      // Create new sales order with updated fields
      const newOrderData = {
        orderDate: cloneFormData.orderDate,
        status: 'OPEN', // Always start as DRAFT for cloned orders
        effectiveStartDate: cloneFormData.effectiveStartDate,
        effectiveEndDate: cloneFormData.effectiveEndDate,
        notes: fullOrder.notes || '',
        refNo: cloneFormData.refNo,
        refDate: cloneFormData.refDate,
        totalAmount: fullOrder.totalAmount || 0,
        taxAmount: fullOrder.taxAmount || 0,
        grandTotal: fullOrder.grandTotal || 0,
        isActive: fullOrder.isActive ?? true,
      };

      // Create the new sales order
      const createResponse = await salesOrderApiService.createSalesOrder(
        newOrderData,
        fullOrder.customerId,
        fullOrder.branchId
      );

      if (!createResponse.success || !createResponse.data) {
        throw new Error('Failed to create cloned order');
      }

      const newOrderId = createResponse.data.id;

      // Clone line items if any exist; only include items with a valid endgoodId
      if (lineItems.length > 0) {
        const batchData = lineItems
          .map((item: SalesOrderDetail) => {
            const endgoodId = item.endgoodId ?? item.endgood?.id;
            if (endgoodId == null || Number(endgoodId) === 0) return null;
            const endgoodCategoryId = item.endgoodCategoryId ?? item.endgood?.endgoodCategory?.id ?? item.endgoodCategory?.id ?? 0;
            return {
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discountPercentage: item.discountPercentage,
              discountAmount: item.discountAmount,
              taxPercentage: item.taxPercentage,
              taxAmount: item.taxAmount,
              lineTotal: item.lineTotal,
              salesOrderId: newOrderId,
              endgoodId: Number(endgoodId),
              endgoodCategoryId: Number(endgoodCategoryId),
            };
          })
          .filter((d): d is NonNullable<typeof d> => d != null);

        if (batchData.length > 0) {
          await salesOrderApiService.batchCreateSalesOrderDetails(newOrderId.toString(), batchData);
        }
      }

      toast.success('Sales order cloned successfully!');
      setCloneDialogOpen(false);
      setOrderToClone(null);
      
      // Refresh the list
      loadSalesOrders();
    } catch (error) {
      // console.error('Error cloning sales order:', error);
      toast.error('Failed to clone sales order');
    } finally {
      setCloning(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string | null | undefined) => {
    if (!status) {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (status.toUpperCase()) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'SHIPPED':
        return 'bg-purple-100 text-purple-800';
      case 'DELIVERED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'CLOSED':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string | null | undefined) => {
    if (!priority) {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'urgent':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate statistics
  const totalOrders = totalCount;
  const inProgressOrders = filteredSalesOrders.filter(order => order.status?.toUpperCase() === 'IN_PROGRESS').length;
  const deliveredOrders = filteredSalesOrders.filter(order => order.status?.toUpperCase() === 'DELIVERED').length;
  const totalValue = filteredSalesOrders.reduce((sum, order) => {
    return sum + (order.grandTotal || 0);
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading sales orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Sales Order Management</CardTitle>
                <p className="text-primary-100 text-sm">Manage your sales orders and deliveries</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={loading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <PermissionGate route={SALES_ORDER_ROUTE} action="create">
                <Button
                  onClick={handleNewOrder}
                  className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Sales Order
                </Button>
              </PermissionGate>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search sales orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {currentPageData.length} of {totalCount} orders
            </div>
          </div>

          {/* Sales Orders Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                <p className="text-gray-600">Loading sales orders...</p>
              </div>
            ) : currentPageData.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No sales orders found</p>
                <p className="text-gray-400">Create your first sales order to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableHead>Order Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Delivery Date</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPageData.map((order) => (
                    <TableRow key={order.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{order.soNumber || `SO-${order.id.toString().padStart(3, '0')}`}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-gray-400" />
                          <span>{order.customerName || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span>{formatDate(order.orderDate)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span>{formatDate(order.effectiveEndDate)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                          {order.itemCount ? `${order.itemCount} items` : 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getPriorityColor('Medium')} border-0 text-xs`}>
                          Medium
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(order.status)} border-0 text-xs`}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewOrder(order)}
                            className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <PermissionGate route={SALES_ORDER_ROUTE} action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditOrder(order)}
                              className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={SALES_ORDER_ROUTE} action="create">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCloneOrder(order)}
                              className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                              title="Clone Sales Order"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={SALES_ORDER_ROUTE} action="delete">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteOrder(order)}
                              className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Clone Sales Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {orderToClone && (
              <div className="text-sm text-primary-700 font-medium rounded-md bg-primary-50 border border-primary-200 px-3 py-2 space-y-1">
                <p>
                  Cloning from: <span className="font-semibold">{orderToClone.soNumber ?? `#${orderToClone.id}`}</span>
                </p>
                <p>
                  Customer: <span className="font-semibold">{orderToClone.customerName ?? '—'}</span>
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="clone-orderDate">Order Date *</Label>
              <Input
                id="clone-orderDate"
                type="date"
                value={cloneFormData.orderDate}
                onChange={(e) => setCloneFormData(prev => ({ ...prev, orderDate: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clone-effectiveStartDate">Effective From *</Label>
              <Input
                id="clone-effectiveStartDate"
                type="date"
                value={cloneFormData.effectiveStartDate}
                onChange={(e) => setCloneFormData(prev => ({ ...prev, effectiveStartDate: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clone-effectiveEndDate">Effective End *</Label>
              <Input
                id="clone-effectiveEndDate"
                type="date"
                value={cloneFormData.effectiveEndDate}
                onChange={(e) => setCloneFormData(prev => ({ ...prev, effectiveEndDate: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clone-refNo">Ref Number</Label>
              <Input
                id="clone-refNo"
                type="text"
                value={cloneFormData.refNo}
                onChange={(e) => setCloneFormData(prev => ({ ...prev, refNo: e.target.value }))}
                placeholder="Enter reference number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clone-refDate">Ref Date *</Label>
              <Input
                id="clone-refDate"
                type="date"
                value={cloneFormData.refDate}
                onChange={(e) => setCloneFormData(prev => ({ ...prev, refDate: e.target.value }))}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCloneDialogOpen(false);
                setOrderToClone(null);
              }}
              disabled={cloning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloneSubmit}
              disabled={cloning || !cloneFormData.orderDate || !cloneFormData.effectiveStartDate || !cloneFormData.effectiveEndDate || !cloneFormData.refDate}
              className="bg-primary-600 hover:bg-primary-700"
            >
              {cloning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cloning...
                </>
              ) : (
                'Clone Order'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        {/* Summary Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-white shadow-md rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Total Orders</p>
                  <p className="text-3xl font-bold text-pink-600">{totalOrders}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-pink-100 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-pink-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">In Progress</p>
                  <p className="text-3xl font-bold text-orange-600">{inProgressOrders}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Delivered</p>
                  <p className="text-3xl font-bold text-green-600">{deliveredOrders}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Total Value</p>
                  <p className="text-3xl font-bold text-blue-600">₹{totalValue.toLocaleString()}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}